"use client";

import { Link } from "@/navigation";
import { useTranslations } from "next-intl";
import Image from "next/image";

const CTA_ACCENT = "#70B32A";
/** 背景网格线条色 */
const CTA_PATTERN_LINE = "#ede5e4";
/** 网格线间距（透明区宽度，线条占其后 1px） */
const CTA_PATTERN_GAP_PX = 80;

const STAMPING_UPLOAD_CTA_IMAGES = [
	"/image/new-version/fabrication/metal-stamping/横幅/metal-stamping-1.webp",
	"/image/new-version/fabrication/metal-stamping/横幅/metal-stamping-2.webp",
	"/image/new-version/fabrication/metal-stamping/横幅/metal-stamping-3.webp",
] as const;

const WELDING_UPLOAD_CTA_IMAGES = [
	"/image/new-version/fabrication/metal-welding/横幅/sheet-metal-parts-1.webp",
	"/image/new-version/fabrication/metal-welding/横幅/sheet-metal-parts-2.webp",
	"/image/new-version/fabrication/metal-welding/横幅/sheet-metal-parts-3.webp",
] as const;

const METAL_CASTING_UPLOAD_CTA_IMAGES = [
	"/image/new-version/casting/Metal-Casting/横幅/metal-casting-1.webp",
	"/image/new-version/casting/Metal-Casting/横幅/metal-casting-2.webp",
	"/image/new-version/casting/Metal-Casting/横幅/metal-casting-3.webp",
] as const;

const INVESTMENT_CASTING_UPLOAD_CTA_IMAGES = [
	"/image/new-version/casting/Investment-Casting/横幅/investment-casting-1.webp",
	"/image/new-version/casting/Investment-Casting/横幅/investment-casting2.webp",
	"/image/new-version/casting/Investment-Casting/横幅/investment-casting3.webp",
] as const;

const HIGH_PRESSURE_CASTING_UPLOAD_CTA_IMAGES = [
	"/image/new-version/casting/High-Pressure-Die-Casting/横幅/high-pressure-die-casting-1.webp",
	"/image/new-version/casting/High-Pressure-Die-Casting/横幅/high-pressure-die-casting-2.webp",
	"/image/new-version/casting/High-Pressure-Die-Casting/横幅/high-pressure-die-casting-3.webp",
] as const;

const LOW_PRESSURE_CASTING_UPLOAD_CTA_IMAGES = [
	"/image/new-version/casting/Low-Pressure-Die-Casting/横幅/low-pressure-die-casting-parts-1.webp",
	"/image/new-version/casting/Low-Pressure-Die-Casting/横幅/low-pressure-die-casting-parts-2.webp",
	"/image/new-version/casting/Low-Pressure-Die-Casting/横幅/low-pressure-die-casting-parts-3.webp",
] as const;

const SAND_CASTING_UPLOAD_CTA_IMAGES = [
	"/image/new-version/casting/Sand-Casting/横幅/sand-casting-1.webp",
	"/image/new-version/casting/Sand-Casting/横幅/sand-casting-2.webp",
	"/image/new-version/casting/Sand-Casting/横幅/sand-casting-3.webp",
] as const;

const DIE_CASTING_UPLOAD_CTA_IMAGES = [
	"/image/new-version/casting/Die-Casting/横幅/die-casting-1.webp",
	"/image/new-version/casting/Die-Casting/横幅/die-casting-2.webp",
	"/image/new-version/casting/Die-Casting/横幅/die-casting-3.webp",
] as const;

const VACUUM_CASTING_UPLOAD_CTA_IMAGES = [
	"/image/new-version/casting/Vacuum-Casting/banner/vacuum-casting-1.webp",
	"/image/new-version/casting/Vacuum-Casting/banner/vacuum-casting-2.webp",
	"/image/new-version/casting/Vacuum-Casting/banner/vacuum-casting-3.webp",
] as const;

const THREE_D_PRINTING_UPLOAD_CTA_IMAGES = [
	"/image/new-version/more/3D-Printing/banner/3d-printing-parts-1.webp",
	"/image/new-version/more/3D-Printing/banner/3d-printing-parts-2.webp",
	"/image/new-version/more/3D-Printing/banner/3d-printing-parts-3.webp",
] as const;

const ALUMINUM_EXTRUSION_UPLOAD_CTA_IMAGES = [
	"/image/new-version/more/AluminumExtrusion/横幅/aluminum-extrusion-parts-1.webp",
	"/image/new-version/more/AluminumExtrusion/横幅/aluminum-extrusion-parts-2.webp",
	"/image/new-version/more/AluminumExtrusion/横幅/aluminum-extrusion-parts-3.webp",
] as const;

const SPRING_MANUFACTURING_UPLOAD_CTA_IMAGES = [
	"/image/new-version/more/SpringManufacturing/横幅/spring-manufacturing-parts-1.webp",
	"/image/new-version/more/SpringManufacturing/横幅/spring-manufacturing-parts-2.webp",
	"/image/new-version/more/SpringManufacturing/横幅/spring-manufacturing-parts-3.webp",
] as const;

const SURFACE_FINISHING_UPLOAD_CTA_IMAGES = [
	"/image/new-version/ValueAddedSolutions/surface-finishing/横幅/surface-finishing-parts-1.webp",
	"/image/new-version/ValueAddedSolutions/surface-finishing/横幅/surface-finishing-parts-2.webp",
	"/image/new-version/ValueAddedSolutions/surface-finishing/横幅/surface-finishing-parts-13.webp",
] as const;

const ON_DEMAND_MANUFACTURING_UPLOAD_CTA_IMAGES = [
	"/image/new-version/ValueAddedSolutions/On-Demand-Manufacturing/横幅/14-js-cnc-parts-01.webp",
	"/image/new-version/ValueAddedSolutions/On-Demand-Manufacturing/横幅/15-js-cnc-parts-02.webp",
	"/image/new-version/ValueAddedSolutions/On-Demand-Manufacturing/横幅/16-js-die-casting-01.webp",
] as const;

const ASSEMBLY_UPLOAD_CTA_IMAGES = [
	"/image/new-version/ValueAddedSolutions/assembly/横幅/assembly-1.webp",
	"/image/new-version/ValueAddedSolutions/assembly/横幅/assembly-2.webp",
	"/image/new-version/ValueAddedSolutions/assembly/横幅/assembly-3.webp",
] as const;

const INDUSTRIAL_AUTOMATION_UPLOAD_CTA_IMAGES = [
	"/image/new-version/ValueAddedSolutions/industrial-automation/横幅/cnc-machining-parts-3.webp",
	"/image/new-version/ValueAddedSolutions/industrial-automation/横幅/cnc-machining-parts-5.webp",
	"/image/new-version/ValueAddedSolutions/industrial-automation/横幅/cnc-machining-parts-6.webp",
] as const;

const SLA_UPLOAD_CTA_IMAGES = [
	"/image/new-version/more/SLA/banner/sla-parts-1.webp",
	"/image/new-version/more/SLA/banner/sla-parts-2.webp",
	"/image/new-version/more/SLA/banner/sla-parts-3.webp",
] as const;

const SLS_UPLOAD_CTA_IMAGES = [
	"/image/new-version/more/SLS/banner/sls-parts-1.webp",
	"/image/new-version/more/SLS/banner/sls-parts-2.webp",
	"/image/new-version/more/SLS/banner/sls-parts-3.webp",
] as const;

const FDM_UPLOAD_CTA_IMAGES = [
	"/image/new-version/more/FDM/banner/fdm-parts-1.webp",
	"/image/new-version/more/FDM/banner/fdm-parts-2.webp",
	"/image/new-version/more/FDM/banner/fdm-parts-3.webp",
] as const;

const MJF_UPLOAD_CTA_IMAGES = [
	"/image/new-version/more/MJF/banner/mjf-parts-1.webp",
	"/image/new-version/more/MJF/banner/mjf-parts-2.webp",
	"/image/new-version/more/MJF/banner/mjf-parts-3.webp",
] as const;

const UPLOAD_CTA_BY_SLUG: Record<string, { namespace: string; images: readonly string[] }> = {
	stamping: { namespace: "new-stamping", images: STAMPING_UPLOAD_CTA_IMAGES },
	welding: { namespace: "new-welding", images: WELDING_UPLOAD_CTA_IMAGES },
	"metal-casting": { namespace: "new-metal-casting", images: METAL_CASTING_UPLOAD_CTA_IMAGES },
	"investment-casting": { namespace: "new-investment-casting", images: INVESTMENT_CASTING_UPLOAD_CTA_IMAGES },
	"high-pressure-casting": {
		namespace: "new-high-pressure-casting",
		images: HIGH_PRESSURE_CASTING_UPLOAD_CTA_IMAGES,
	},
	"low-pressure-casting": {
		namespace: "new-low-pressure-casting",
		images: LOW_PRESSURE_CASTING_UPLOAD_CTA_IMAGES,
	},
	"sand-casting": { namespace: "new-sand-casting", images: SAND_CASTING_UPLOAD_CTA_IMAGES },
	"die-casting": { namespace: "new-die-casting", images: DIE_CASTING_UPLOAD_CTA_IMAGES },
	"vacuum-casting": { namespace: "new-vacuum-casting", images: VACUUM_CASTING_UPLOAD_CTA_IMAGES },
	"3d-printing": { namespace: "new-3d-printing", images: THREE_D_PRINTING_UPLOAD_CTA_IMAGES },
	"aluminum-extrusion": {
		namespace: "new-aluminum-extrusion",
		images: ALUMINUM_EXTRUSION_UPLOAD_CTA_IMAGES,
	},
	"spring-manufacturing": {
		namespace: "new-spring-manufacturing",
		images: SPRING_MANUFACTURING_UPLOAD_CTA_IMAGES,
	},
	"surface-finishing": {
		namespace: "new-surface-finishing",
		images: SURFACE_FINISHING_UPLOAD_CTA_IMAGES,
	},
	"on-demand-manufacturing": {
		namespace: "new-on-demand-manufacturing",
		images: ON_DEMAND_MANUFACTURING_UPLOAD_CTA_IMAGES,
	},
	assembly: {
		namespace: "new-assembly",
		images: ASSEMBLY_UPLOAD_CTA_IMAGES,
	},
	"industrial-automation": {
		namespace: "new-industrial-automation",
		images: INDUSTRIAL_AUTOMATION_UPLOAD_CTA_IMAGES,
	},
	"sla-stereolithography": { namespace: "new-sla-stereolithography", images: SLA_UPLOAD_CTA_IMAGES },
	"sls-selective-laser-sintering": {
		namespace: "new-sls-selective-laser-sintering",
		images: SLS_UPLOAD_CTA_IMAGES,
	},
	"fdm-fused-deposition-modeling": {
		namespace: "new-fdm-fused-deposition-modeling",
		images: FDM_UPLOAD_CTA_IMAGES,
	},
	"mjf-multi-jet-fusion": { namespace: "new-mjf-multi-jet-fusion", images: MJF_UPLOAD_CTA_IMAGES },
};

const UPLOAD_CTA_DEFAULT = { namespace: "new-stamping", images: STAMPING_UPLOAD_CTA_IMAGES };

const UploadDesignCta = ({ slug = "stamping" }: { slug?: string }) => {
	const { namespace, images } = UPLOAD_CTA_BY_SLUG[slug] ?? UPLOAD_CTA_DEFAULT;
	const t = useTranslations(namespace);

	return (
		<section className="relative overflow-hidden bg-[#ededed] py-8 sm:py-10 md:py-12 lg:py-16">
			{/* 浅灰等距/三角网格底纹 */}
			<div
				className="pointer-events-none absolute inset-0"
				style={{
					backgroundColor: "#ededed",
					backgroundImage: `
						repeating-linear-gradient(30deg, transparent, transparent ${CTA_PATTERN_GAP_PX}px, ${CTA_PATTERN_LINE} ${CTA_PATTERN_GAP_PX}px, ${CTA_PATTERN_LINE} ${
							CTA_PATTERN_GAP_PX + 1
						}px),
						repeating-linear-gradient(-30deg, transparent, transparent ${CTA_PATTERN_GAP_PX}px, ${CTA_PATTERN_LINE} ${CTA_PATTERN_GAP_PX}px, ${CTA_PATTERN_LINE} ${
							CTA_PATTERN_GAP_PX + 1
						}px),
						repeating-linear-gradient(90deg, transparent, transparent ${CTA_PATTERN_GAP_PX}px, ${CTA_PATTERN_LINE} ${CTA_PATTERN_GAP_PX}px, ${CTA_PATTERN_LINE} ${
							CTA_PATTERN_GAP_PX + 1
						}px)
					`,
				}}
			/>

			<div className="container relative z-10">
				{/* 左侧单张大图，略倾斜 */}
				<div className="pointer-events-none absolute bottom-0 left-4 z-[1] hidden w-[200px] -rotate-[8deg] drop-shadow-[0_18px_32px_rgba(0,0,0,0.18)] lg:block xl:w-[240px]">
					<Image
						src={images[0]}
						alt={t("uploadDesignCtaImageAlt1")}
						width={480}
						height={400}
						className="h-auto w-full object-contain object-bottom"
					/>
				</div>

				{/* 右侧两张零件图错落 */}
				<div className="pointer-events-none absolute bottom-2 right-4 z-[1] hidden items-end gap-4 lg:flex xl:bottom-4">
					<div className="w-[170px] -translate-y-6 drop-shadow-[0_16px_28px_rgba(0,0,0,0.16)] xl:w-[140px] xl:-translate-y-8">
						<Image
							src={images[1]}
							alt={t("uploadDesignCtaImageAlt2")}
							width={400}
							height={360}
							className="h-auto w-full object-contain object-bottom"
						/>
					</div>
					<div className="w-[150px] drop-shadow-[0_14px_26px_rgba(0,0,0,0.15)] xl:w-[180px]">
						<Image
							src={images[2]}
							alt={t("uploadDesignCtaImageAlt3")}
							width={350}
							height={320}
							className="h-auto w-full object-contain object-bottom"
						/>
					</div>
				</div>

				<div className="relative text-center">
					{/* 第一行：标题 + 绿色短横线延伸 */}
					<div className="mb-2 flex flex-wrap items-end justify-center gap-x-2 gap-y-1 sm:gap-x-3">
						<span className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
							{t("uploadDesignCtaTitle1")}
						</span>
						<span
							className="mb-[0.35em] inline-block h-[3px] w-10 shrink-0 sm:w-16 md:mb-[0.4em] md:w-20 lg:w-28"
							style={{ backgroundColor: CTA_ACCENT }}
							aria-hidden
						/>
					</div>
					<h2 className="mb-6 text-2xl font-bold leading-tight text-black sm:mb-8 sm:text-[30px] md:mb-10 md:text-4xl lg:mb-12">
						{t("uploadDesignCtaTitle2")}
					</h2>

					<Link
						href="/upload"
						className="inline-flex items-center justify-center rounded-md bg-[#70B32A] px-8 py-3.5 text-base font-semibold text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#83b751] hover:text-[#f7ffee] hover:shadow-lg sm:px-10 sm:py-4 sm:text-lg"
					>
						{t("uploadDesignCtaGetQuote")}
					</Link>
				</div>
			</div>
		</section>
	);
};

export default UploadDesignCta;
